import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	Divider,
	Grid,
	Paper,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIcon from "@mui/icons-material/Phone";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AvatarPersona } from "./AvatarPersona";
import { getHistorialFichajesPersona } from "../../services/apis";
import { api } from "../../services/apiClient";
import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { formatDateTimeEs, formatScoreOverHundred } from "../../utils/formatters";

const getEstadoColor = (estado) => {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
};

const getEstadoLabel = (estado) => {
	switch (estado) {
		case "pendiente":
			return "Pendiente";
		case "comprobado":
			return "Comprobado";
		case "validado":
			return "Validado";
		case "error":
			return "Error";
		default:
			return estado;
	}
};

const getInitialPersona = (statePersona, idUsuario) => {
	if (statePersona?.id) return statePersona;

	return {
		id: idUsuario,
		nombre: "",
		apellidos: "",
		telefono: "",
		nif: "",
		mail: "",
		puede_fichar: false,
		empresa: "",
		grupo: "",
		grupo_autofichaje: "",
		nombre_rol: "",
		imagen_url: null,
	};
};

const InfoField = ({ icon, label, value }) => (
	<Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
		<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
				{icon}
				<Typography variant="subtitle2" color="text.secondary">
					{label}
				</Typography>
			</Box>
			<Typography variant="body1">{value || "-"}</Typography>
		</Paper>
	</Grid>
);

export const DetallePersona = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const idUsuario = Number(id);
	const [tab, setTab] = useState("info");
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [persona, setPersona] = useState(() => getInitialPersona(location.state?.persona, idUsuario));
	const [historial, setHistorial] = useState([]);
	const [historialLoading, setHistorialLoading] = useState(false);
	const [historialError, setHistorialError] = useState("");
	const [historialPage, setHistorialPage] = useState(1);
	const [historialHasMore, setHistorialHasMore] = useState(true);
	const [selectedImage, setSelectedImage] = useState(null);
	const [imageDataUrls, setImageDataUrls] = useState({});

	const subtitulo = useMemo(() => {
		const partes = [persona?.nombre_rol, persona?.empresa].filter(Boolean);
		return partes.length > 0 ? partes.join(" · ") : "Sin rol o empresa";
	}, [persona?.empresa, persona?.nombre_rol]);

	useEffect(() => {
		let active = true;

		const cargarPersona = () => {
			setLoaded(false);
			setError("");

			if (!persona?.id || persona.id !== idUsuario) {
				setError("No hay persona seleccionada");
				setLoaded(true);
				return;
			}

			setLoaded(true);
		};

		cargarPersona();

		return () => {
			active = false;
		};
	}, [idUsuario, persona?.id]);

	const cargarHistorial = async (page = 1) => {
		if (!persona?.id) return;

		setHistorialLoading(true);
		setHistorialError("");

		try {
			const response = await getHistorialFichajesPersona(persona.id, page, 40);

			if (response?.success) {
				const nuevosItems = response.data || [];
				if (page === 1) {
					setHistorial(nuevosItems);
				} else {
					setHistorial(prev => [...prev, ...nuevosItems]);
				}
				setHistorialHasMore(nuevosItems.length === 40);
				setHistorialPage(page);
			} else {
				setHistorialError(response?.message || "No se pudo cargar el historial");
			}
		} catch (err) {
			console.error("Error cargando historial:", err);
			setHistorialError("No se pudo cargar el historial");
		} finally {
			setHistorialLoading(false);
		}
	};

	useEffect(() => {
		if (tab === "historial" && persona?.id && historial.length === 0) {
			cargarHistorial(1);
		}
	}, [tab, persona?.id]);

	const fetchImageAsDataUrl = async (url, key) => {
		try {
			const response = await api.get(url, { responseType: 'blob' });
			const blob = response.data;
			const dataUrl = await new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result);
				reader.readAsDataURL(blob);
			});
			setImageDataUrls(prev => ({ ...prev, [key]: dataUrl }));
		} catch (error) {
			console.error('Error fetching image:', error);
		}
	};

	useEffect(() => {
		historial.forEach((item) => {
			if (item.imagen_url && !imageDataUrls[`${item.id_cuadrilla}-${item.fecha_comprobacion}`]) {
				fetchImageAsDataUrl(item.imagen_url, `${item.id_cuadrilla}-${item.fecha_comprobacion}`);
			}
		});
	}, [historial]);

	const handleAvatarUploaded = (updatedPersona) => {
		setPersona((current) => ({
			...current,
			...updatedPersona,
		}));
	};

	const volver = () => {
		if (typeof location.state?.from === "string" && location.state.from.trim()) {
			navigate(location.state.from);
			return;
		}

		navigate("/personas");
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Detalle de persona" />
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
				<Button variant="text" startIcon={<ArrowBackIcon />} onClick={volver}>
					Volver
				</Button>
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined" sx={{ width: "100%", p: { xs: 2, md: 3 } }}>
					<Grid container spacing={3} alignItems="center">
						<Grid
							item
							size={{ xs: 12, md: 3 }}
							sx={{ display: "flex", justifyContent: "center" }}
						>
							<AvatarPersona
								persona={persona}
								size={140}
								allowUpload
								onAvatarUploaded={handleAvatarUploaded}
							/>
						</Grid>

						<Grid item size={{ xs: 12, md: 9 }}>
							<Typography variant="h4" sx={{ mb: 0.5 }}>
								{[persona?.nombre, persona?.apellidos].filter(Boolean).join(" ") || "-"}
							</Typography>

							<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
								{subtitulo}
							</Typography>

							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
								<Chip label={`ID ${persona?.id || "-"}`} variant="outlined" />
								<Chip label={persona?.nif || "Sin NIF"} color="primary" variant="outlined" />
								{persona?.nombre_rol && <Chip label={persona.nombre_rol} />}
								{persona?.nombre_departamento && (
									<Chip label={persona.nombre_departamento} color="secondary" />
								)}
							</Box>

							{/* <Typography variant="body2" color="text.secondary">
								{tieneAvata
									? "Pulsa la foto para verla en grande o usa el icono de cámara para actualizarla."
									: "Pulsa el avatar o el icono de cámara para añadir una foto."}
							</Typography> */}
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Tabs
						value={tab}
						onChange={(_, value) => setTab(value)}
						variant="scrollable"
						allowScrollButtonsMobile
						sx={{ mb: 3 }}
					>
						<Tab label="Informacion" value="info" />
						<Tab label="Historial" value="historial" />
						<Tab label="Documentacion" value="docs" />
					</Tabs>

					{!loaded && (
						<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
							<CircularProgress />
						</Box>
					)}

					{loaded && error && <Alert severity="error">{error}</Alert>}

					{loaded && !error && tab === "info" && (
						<Grid container spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2 }}>Datos personales</Typography>
							</Grid>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="ID"
								value={persona?.id || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="Nombre"
								value={persona?.nombre || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="Apellidos"
								value={persona?.apellidos || "-"}
							/>
							<InfoField
								icon={<PhoneIcon color="primary" fontSize="small" />}
								label="Teléfono"
								value={persona?.telefono || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="NIF"
								value={persona?.nif || "-"}
							/>

							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Permisos y accesos</Typography>
							</Grid>
							<InfoField
								icon={<AlternateEmailIcon color="primary" fontSize="small" />}
								label="Email"
								value={persona?.mail || "-"}
							/>
							<InfoField
								icon={<AssignmentIndIcon color="primary" fontSize="small" />}
								label="Puede fichar"
								value={persona?.puede_fichar ? "Sí" : "No"}
							/>

							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Información laboral</Typography>
							</Grid>
							<InfoField
								icon={<BusinessIcon color="primary" fontSize="small" />}
								label="Empresa"
								value={persona?.empresa || "-"}
							/>
							<InfoField
								icon={<GroupWorkIcon color="primary" fontSize="small" />}
								label="Grupo"
								value={persona?.grupo || "-"}
							/>
							<InfoField
								icon={<GroupWorkIcon color="primary" fontSize="small" />}
								label="Grupo autofichaje"
								value={persona?.grupo_autofichaje || "-"}
							/>
							<InfoField
								icon={<AssignmentIndIcon color="primary" fontSize="small" />}
								label="Rol"
								value={persona?.nombre_rol || "-"}
							/>
						</Grid>
					)}

					{loaded && !error && tab === "historial" && (
						<Grid container direction="column" spacing={2}>
							{historialError && (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="error">{historialError}</Alert>
									<Button onClick={() => cargarHistorial(1)} sx={{ mt: 1 }}>
										Reintentar
									</Button>
								</Grid>
							)}

							{historial.length === 0 && !historialLoading && !historialError ? (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="info">
										Esta persona no tiene historial de fichajes visible.
									</Alert>
								</Grid>
							) : (
								historial.map((item, index) => (
									<Grid item size={{ xs: 12 }} key={`${item.id_cuadrilla}-${index}`}>
										<Paper variant="outlined" sx={{ p: 2 }}>
											<Grid container spacing={2} alignItems="center">
												{item.imagen_url && (
													<Grid item size={{ xs: 12, md: 2 }}>
														{imageDataUrls[`${item.id_cuadrilla}-${item.fecha_comprobacion}`] ? (
															<Box
																component="img"
																src={imageDataUrls[`${item.id_cuadrilla}-${item.fecha_comprobacion}`]}
																alt="Imagen de fichaje"
																onClick={() => setSelectedImage(imageDataUrls[`${item.id_cuadrilla}-${item.fecha_comprobacion}`])}
																sx={{
																	width: 80,
																	height: 80,
																	objectFit: "cover",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	cursor: "pointer",
																	transition: "transform 0.2s",
																	"&:hover": {
																		transform: "scale(1.05)",
																	},
																}}
															/>
														) : (
															<Box
																sx={{
																	width: 80,
																	height: 80,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	backgroundColor: "grey.100",
																}}
															>
																<CircularProgress size={24} />
															</Box>
														)}
													</Grid>
												)}

												<Grid item size={{ xs: 12, md: item.imagen_url ? 4 : 6 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
														<GroupWorkIcon color="warning" fontSize="small" />
														<Typography variant="subtitle1">
															{item.nombre_cuadrilla || `Parte ${item.id_cuadrilla}`}
														</Typography>
													</Box>
													<Typography variant="body2" color="text.secondary">
														Cliente: {item.nombre_cliente || "-"}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 12, md: item.imagen_url ? 3 : 3 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
														<EventIcon color="primary" fontSize="small" />
														<Typography variant="body2">
															{formatDateTimeEs(item.fecha_comprobacion || item.fecha_cuadrilla)}
														</Typography>
													</Box>
												</Grid>

												<Grid item size={{ xs: 12, md: item.imagen_url ? 3 : 3 }}>
													<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "start", md: "end" } }}>
														<Chip
															label={getEstadoLabel(item.estado_comprobacion || "pendiente")}
															color={getEstadoColor(item.estado_comprobacion)}
															size="small"
														/>
														{item.puntuacion_comprobacion && (
															<Chip
																label={`Similitud: ${formatScoreOverHundred(item.puntuacion_comprobacion)}`}
																size="small"
																variant="outlined"
															/>
														)}
													</Box>
												</Grid>
											</Grid>
										</Paper>
									</Grid>
								))
							)}

							{historialLoading && (
								<Grid item size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "center", py: 2 }}>
									<CircularProgress />
								</Grid>
							)}

							{historialHasMore && !historialLoading && historial.length > 0 && (
								<Grid item size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "center" }}>
									<Button
										variant="outlined"
										onClick={() => cargarHistorial(historialPage + 1)}
									>
										Cargar más
									</Button>
								</Grid>
							)}
						</Grid>
					)}

					{loaded && !error && tab === "docs" && (
						<Grid container spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Alert severity="info">
									La documentacion por persona en web todavia no tiene endpoint especifico.
									La pestaña queda preparada para conectarla cuando ese dato este disponible.
								</Alert>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
										<InsertDriveFileIcon color="action" />
										<Typography variant="subtitle2">Estado actual</Typography>
									</Box>
									<Typography variant="body2" color="text.secondary">
										Pendiente de integrar los documentos individuales del usuario {persona?.id || "-"}.
									</Typography>
								</Paper>
							</Grid>
						</Grid>
					)}
				</Paper>
			</Grid>

			<Dialog
				open={!!selectedImage}
				onClose={() => setSelectedImage(null)}
				maxWidth="md"
				fullWidth
			>
				<DialogContent sx={{ p: 1, display: "flex", justifyContent: "center" }}>
					{selectedImage && (
						<Box
							component="img"
							src={selectedImage}
							alt="Imagen ampliada de fichaje"
							sx={{
								maxWidth: "100%",
								maxHeight: "80vh",
								objectFit: "contain",
							}}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Grid>
	);
};
